import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

/**
 * Tracks user activity by calling touch_user_activity() on login
 * and periodically (every 30 min) while the app is open.
 * This powers the 7-day inactivity re-engagement reminder system.
 */
export function useActivityTracker() {
  const { user } = useAuth();
  const lastTouchRef = useRef<number>(0);

  useEffect(() => {
    if (!user) return;

    const touch = async () => {
      const now = Date.now();
      // Debounce: don't touch more than once per 30 minutes
      if (now - lastTouchRef.current < 30 * 60 * 1000) return;
      lastTouchRef.current = now;

      try {
        await supabase.rpc('touch_user_activity');
      } catch (err) {
        console.error('Failed to track activity:', err);
      }
    };

    // Touch on mount (login/app open)
    touch();

    // Touch every 30 minutes while active
    const interval = setInterval(touch, 30 * 60 * 1000);

    // Touch on visibility change (returning to tab)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        touch();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user]);
}
